import type { ReactNode } from 'react';
import { cx } from '@/lib/cx';
import type { RoundStatus } from '@/lib/api/types';
import { t, type PlainMessageKey } from '@/lib/i18n';
import styles from './Pill.module.css';
import { ChevronDownIcon } from '@/components/icons';

type Tone = 'default' | 'onGreen' | 'onInk';

interface PillProps {
  children: ReactNode;
  selected?: boolean;
  tone?: Tone;
  /** Colour dot rendered before the label — used for tee boxes. */
  dotColor?: string;
  /** Adds a ▾ caret, marking the pill as a dropdown trigger. */
  caret?: boolean;
  onClick?: () => void;
  className?: string;
}

function toneClass(tone: Tone, selected: boolean): string | undefined {
  if (tone === 'onGreen') return selected ? styles.onGreenSelected : styles.onGreen;
  if (tone === 'onInk') return styles.onInk;
  return selected ? styles.selected : styles.unselected;
}

export function Pill({
  children,
  selected = false,
  tone = 'default',
  dotColor,
  caret = false,
  onClick,
  className,
}: PillProps) {
  const content = (
    <>
      {dotColor && (
        <span
          className={styles.dot}
          style={{
            background: dotColor,
            // A white or near-white tee needs an edge to exist against a white surface.
            boxShadow: isNearWhite(dotColor) ? 'inset 0 0 0 1px var(--border-soft)' : undefined,
          }}
        />
      )}
      {children}
      {caret && <ChevronDownIcon size={13} className={styles.caret} />}
    </>
  );

  const cls = cx(styles.pill, toneClass(tone, selected), className);

  if (!onClick) return <span className={cls}>{content}</span>;

  return (
    <button type="button" className={cls} onClick={onClick} aria-pressed={selected}>
      {content}
    </button>
  );
}

function isNearWhite(hex: string): boolean {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m?.[1]) return false;
  const v = parseInt(m[1], 16);
  const [r, g, b] = [(v >> 16) & 255, (v >> 8) & 255, v & 255];
  return r > 220 && g > 220 && b > 220;
}

const STATUS_STYLE: Record<RoundStatus, string | undefined> = {
  completed: styles.completed,
  in_progress: styles.inProgress,
  abandoned: styles.abandoned,
};

const STATUS_LABEL: Record<RoundStatus, PlainMessageKey> = {
  completed: 'pill.status.completed',
  in_progress: 'pill.status.inProgress',
  abandoned: 'pill.status.abandoned',
};

export function StatusPill({ status }: { status: RoundStatus }) {
  return <span className={cx(styles.pill, STATUS_STYLE[status])}>{t(STATUS_LABEL[status])}</span>;
}

export function VerifiedPill() {
  return (
    <span className={cx(styles.pill, styles.verified)} title={t('pill.verifiedTitle')}>
      {t('pill.verified')}
    </span>
  );
}

export function PillGroup({
  children,
  wrap = false,
  scroll = false,
  className,
  label,
}: {
  children: ReactNode;
  wrap?: boolean;
  scroll?: boolean;
  className?: string;
  label?: string;
}) {
  return (
    <div
      className={cx(styles.group, wrap && styles.groupWrap, scroll && 'scroll-x', className)}
      role="group"
      aria-label={label}
    >
      {children}
    </div>
  );
}
